// Antwortautomatik-Editor – Port von renderAnswerRulesEditor aus
// frontend/alt/wartungen/kategorie-einstellungen.js, erweitert um den
// Rücksprung (source_reset_value).

import { Button } from "@/components/ui/Button";
import { Checkbox, SelectInput, TextInput } from "@/components/ui/Field";
import {
  BEHAVIOR_OPERATOR_LABELS,
  BEHAVIOR_SOURCE_SCOPE_LABELS,
  BEHAVIOR_SOURCE_SCOPES,
  coerceRuleValueForOperator,
  getAvailableSourceFields,
  isBooleanSourceField,
  normalizeSourceScope,
  operatorChoicesForSourceField,
  type SourceFieldMeta,
} from "./behaviorRules";
import {
  ANSWER_RULE_OVERWRITE_MODES,
  ANSWER_RULE_OVERWRITE_MODE_LABELS,
  ANSWER_RULE_TYPES,
  ANSWER_RULE_TYPE_LABELS,
  getAnswerRulesFromBehavior,
  normalizeAnswerRuleConfig,
  type AnswerRule,
  type AnswerRuleCondition,
  type AnswerRuleType,
} from "./answerRules";
import { type BuilderField, type FieldOption, normalizeBehavior } from "./fieldModel";
import { RuleValueInput } from "./RuleValueInput";

function parseRuleValue(raw: string): unknown {
  const value = raw.trim();
  if (!value) return "";
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

function activeFieldOptions(field: BuilderField): { value: string; label: string }[] {
  const raw = field.options_override_json?.options;
  const rows = Array.isArray(raw) ? (raw as FieldOption[]) : [];
  return rows
    .filter((row) => row.active !== false)
    .map((row) => ({
      value: String(row.value || "").trim(),
      label: String(row.label || row.value || "").trim(),
    }))
    .filter((row) => row.value);
}

export function AnswerRulesEditor({
  field,
  allFields,
  onPatch,
}: {
  field: BuilderField;
  allFields: BuilderField[];
  onPatch: (mutator: (f: BuilderField) => void) => void;
}) {
  const rules = getAnswerRulesFromBehavior(field.behavior_override_json);
  const config = normalizeAnswerRuleConfig(field.behavior_override_json?.answer_rule_config);
  const availableFields = getAvailableSourceFields(allFields, field);

  function setRules(next: AnswerRule[]) {
    onPatch((f) => {
      f.behavior_override_json = {
        ...normalizeBehavior(f.behavior_override_json),
        answer_rules: next,
      };
    });
  }

  function updateRule(idx: number, mutator: (rule: AnswerRule) => AnswerRule) {
    setRules(rules.map((rule, i) => (i === idx ? mutator(structuredClone(rule)) : rule)));
  }

  function setConfig(partial: Partial<typeof config>) {
    onPatch((f) => {
      f.behavior_override_json = {
        ...normalizeBehavior(f.behavior_override_json),
        answer_rule_config: { ...config, ...partial },
      };
    });
  }

  function addRule() {
    setRules([
      ...rules,
      {
        rule_type: "setAnswerIf",
        priority: 100,
        conditions: [
          {
            source_scope: "inspection",
            source_field_key: "",
            operator: "eq",
            value: "",
          },
        ],
        target_value: "",
      },
    ]);
  }

  return (
    <div className="mb-editor-section">
      <h5 className="mb-editor-section__title">Antwortautomatik</h5>
      <p className="mb-2 text-xs text-label-secondary">
        Setzt die Antwort dieses Feldes automatisch, wenn Bedingungen erfüllt sind. Mehrere
        Bedingungen innerhalb einer Regel werden mit UND verknüpft.
      </p>
      <div className="mb-3 grid items-end gap-3 sm:grid-cols-2">
        <SelectInput
          label="Bei Regel-Treffer"
          value={config.overwrite_mode}
          onChange={(e) =>
            setConfig({
              overwrite_mode: e.target.value === "manual" ? "manual" : "force",
            })
          }
        >
          {ANSWER_RULE_OVERWRITE_MODES.map((mode) => (
            <option key={mode} value={mode}>
              {ANSWER_RULE_OVERWRITE_MODE_LABELS[mode]}
            </option>
          ))}
        </SelectInput>
        <Checkbox
          id={`ar-clear-${field.uid}`}
          label="Wenn keine Regel passt: Antwort leeren"
          checked={config.clear_when_not_matching}
          onChange={(e) => setConfig({ clear_when_not_matching: e.target.checked })}
        />
      </div>

      {rules.length === 0 ? (
        <p className="text-xs text-label-secondary">Keine Antwort-Regeln konfiguriert.</p>
      ) : (
        <div className="space-y-3">
          {rules.map((rule, ruleIdx) => (
            <AnswerRuleRow
              key={ruleIdx}
              field={field}
              rule={rule}
              availableFields={availableFields}
              onUpdate={(mutator) => updateRule(ruleIdx, mutator)}
              onRemove={() => setRules(rules.filter((_, i) => i !== ruleIdx))}
            />
          ))}
        </div>
      )}
      <Button type="button" size="sm" variant="secondary" className="mt-2" onClick={addRule}>
        Antwort-Regel hinzufügen
      </Button>
    </div>
  );
}

function AnswerRuleRow({
  field,
  rule,
  availableFields,
  onUpdate,
  onRemove,
}: {
  field: BuilderField;
  rule: AnswerRule;
  availableFields: SourceFieldMeta[];
  onUpdate: (mutator: (rule: AnswerRule) => AnswerRule) => void;
  onRemove: () => void;
}) {
  const allowsMultipleConditions = rule.rule_type === "setAnswerIfAll";
  const conditions = allowsMultipleConditions ? rule.conditions : rule.conditions.slice(0, 1);
  const firstCondition = conditions[0] ?? null;
  const resetSourceField = firstCondition
    ? availableFields.find(
        (f) => f.scope === firstCondition.source_scope && f.key === firstCondition.source_field_key,
      ) ?? null
    : null;
  const allowsSourceReset = conditions.length === 1 && Boolean(resetSourceField);

  function updateCondition(idx: number, patch: Partial<AnswerRuleCondition>) {
    onUpdate((r) => {
      const next = [...r.conditions];
      const current = next[idx];
      if (!current) return r;
      next[idx] = { ...current, ...patch };
      return { ...r, conditions: next };
    });
  }

  return (
    <div className="rounded-lg border border-separator p-3">
      <div className="grid items-end gap-2 sm:grid-cols-4">
        <SelectInput
          label="Regeltyp"
          value={rule.rule_type}
          onChange={(e) =>
            onUpdate((r) => ({
              ...r,
              rule_type: (ANSWER_RULE_TYPES as readonly string[]).includes(e.target.value)
                ? (e.target.value as AnswerRuleType)
                : "setAnswerIf",
            }))
          }
        >
          {ANSWER_RULE_TYPES.map((type) => (
            <option key={type} value={type}>
              {ANSWER_RULE_TYPE_LABELS[type]}
            </option>
          ))}
        </SelectInput>
        <TextInput
          label="Priorität"
          type="number"
          step={1}
          value={String(rule.priority)}
          onChange={(e) => {
            const parsed = Number.parseInt(e.target.value, 10);
            onUpdate((r) => ({ ...r, priority: Number.isInteger(parsed) ? parsed : 100 }));
          }}
        />
        <TargetValueControl
          field={field}
          value={rule.target_value}
          onChange={(value) => onUpdate((r) => ({ ...r, target_value: value }))}
        />
        <div className="flex justify-end gap-2">
          {allowsMultipleConditions ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() =>
                onUpdate((r) => ({
                  ...r,
                  conditions: [
                    ...r.conditions,
                    { source_scope: "inspection", source_field_key: "", operator: "eq", value: "" },
                  ],
                }))
              }
            >
              Bedingung +
            </Button>
          ) : null}
          <Button type="button" size="sm" variant="ghost" onClick={onRemove}>
            Regel löschen
          </Button>
        </div>
      </div>

      <div className="mt-2 space-y-2">
        {conditions.map((condition, condIdx) => {
          const sourceScope = normalizeSourceScope(condition.source_scope);
          const scopeFields = availableFields.filter((f) => f.scope === sourceScope);
          const sourceField = scopeFields.find((f) => f.key === condition.source_field_key) ?? null;
          return (
            <div key={condIdx} className="grid items-end gap-2 rounded-lg bg-fill-tertiary/40 p-2 sm:grid-cols-5">
              <SelectInput
                label="Bereich"
                value={sourceScope}
                onChange={(e) =>
                  updateCondition(condIdx, {
                    source_scope: normalizeSourceScope(e.target.value),
                    source_field_key: "",
                    value: "",
                  })
                }
              >
                {BEHAVIOR_SOURCE_SCOPES.map((scope) => (
                  <option key={scope} value={scope}>
                    {BEHAVIOR_SOURCE_SCOPE_LABELS[scope]}
                  </option>
                ))}
              </SelectInput>
              <SelectInput
                label="Wenn Feld"
                value={condition.source_field_key || ""}
                onChange={(e) => {
                  const nextField = scopeFields.find((f) => f.key === e.target.value) ?? null;
                  updateCondition(condIdx, {
                    source_field_key: e.target.value,
                    value: isBooleanSourceField(nextField) ? true : "",
                  });
                }}
              >
                <option value="">Bitte wählen …</option>
                {scopeFields.map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.label} ({s.key})
                  </option>
                ))}
              </SelectInput>
              <SelectInput
                label="Vergleich"
                value={condition.operator || "eq"}
                onChange={(e) => {
                  const operator = e.target.value as AnswerRuleCondition["operator"];
                  updateCondition(condIdx, {
                    operator,
                    value: coerceRuleValueForOperator(operator, condition.value),
                  });
                }}
              >
                {operatorChoicesForSourceField(sourceField, condition.operator).map((op) => (
                  <option key={op} value={op}>
                    {BEHAVIOR_OPERATOR_LABELS[op] || op}
                  </option>
                ))}
              </SelectInput>
              <RuleValueInput
                value={condition.value}
                sourceField={sourceField}
                operator={condition.operator || "eq"}
                onChange={(value) => updateCondition(condIdx, { value })}
              />
              {allowsMultipleConditions ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() =>
                    onUpdate((r) => ({
                      ...r,
                      conditions: r.conditions.filter((_, i) => i !== condIdx),
                    }))
                  }
                >
                  Entfernen
                </Button>
              ) : null}
            </div>
          );
        })}
      </div>

      {allowsSourceReset ? (
        <div className="mt-2 grid items-end gap-2 sm:grid-cols-2">
          <SourceResetControl
            sourceField={resetSourceField}
            hasReset={Object.hasOwn(rule, "source_reset_value")}
            value={rule.source_reset_value}
            onChange={(next) =>
              onUpdate((r) => {
                if (next.active) {
                  return { ...r, source_reset_value: next.value };
                }
                const { source_reset_value: _removed, ...rest } = r;
                return rest as AnswerRule;
              })
            }
          />
          <p className="text-xs text-label-secondary">
            Rücksprung: Wird dieses Feld manuell abweichend von der Regel beantwortet, springt
            „{resetSourceField?.label}“ auf den gewählten Wert.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Zielwert-Control abhängig vom Typ des Zielfeldes (Port von buildAnswerRuleTargetValueControl). */
function TargetValueControl({
  field,
  value,
  onChange,
}: {
  field: BuilderField;
  value: unknown;
  onChange: (next: unknown) => void;
}) {
  const type = field.type;
  const label = "Dann Antwort setzen auf";

  if (type === "bool" || type === "checkbox") {
    const ui = field.ui_override_json || {};
    const labelTrue = String(ui.labelTrue || "Ja").trim() || "Ja";
    const labelFalse = String(ui.labelFalse || "Nein").trim() || "Nein";
    const selected =
      value === true || String(value) === "true"
        ? "true"
        : value === false || String(value) === "false"
          ? "false"
          : "";
    return (
      <SelectInput
        label={label}
        value={selected}
        onChange={(e) => onChange(e.target.value === "" ? "" : e.target.value === "true")}
      >
        <option value="">Bitte wählen …</option>
        <option value="true">{labelTrue} (true)</option>
        <option value="false">{labelFalse} (false)</option>
      </SelectInput>
    );
  }

  if (type === "select" || type === "choice" || type === "article_ref_list") {
    const options = activeFieldOptions(field);
    const normalized = value == null ? "" : String(value);
    const hasCurrent = options.some((opt) => opt.value === normalized);
    const dynamicOptions =
      hasCurrent || !normalized
        ? options
        : [{ value: normalized, label: `${normalized} (aktuell)` }, ...options];
    return (
      <SelectInput label={label} value={normalized} onChange={(e) => onChange(e.target.value)}>
        <option value="">Bitte wählen …</option>
        {dynamicOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </SelectInput>
    );
  }

  if (type === "multiselect") {
    const text = Array.isArray(value) ? JSON.stringify(value) : String(value ?? "");
    return (
      <TextInput
        label={label}
        value={text}
        placeholder='JSON Array, z. B. ["a","b"]'
        onChange={(e) => onChange(parseRuleValue(e.target.value))}
      />
    );
  }

  const text = typeof value === "string" || typeof value === "number" ? String(value) : value == null ? "" : JSON.stringify(value);
  return (
    <TextInput
      label={label}
      value={text}
      placeholder="Antwortwert"
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

/** Rücksprung-Wert für das Quellfeld der (einzigen) Bedingung. */
function SourceResetControl({
  sourceField,
  hasReset,
  value,
  onChange,
}: {
  sourceField: SourceFieldMeta | null;
  hasReset: boolean;
  value: unknown;
  onChange: (next: { active: boolean; value?: unknown }) => void;
}) {
  const label = `Bei manueller Abweichung „${sourceField?.label ?? "Quellfeld"}“ setzen auf`;
  const type = sourceField?.type ?? "text";

  if (type === "bool" || type === "checkbox") {
    const selected = !hasReset
      ? ""
      : value === true || String(value) === "true"
        ? "true"
        : "false";
    return (
      <SelectInput
        label={label}
        value={selected}
        onChange={(e) => {
          if (e.target.value === "") onChange({ active: false });
          else onChange({ active: true, value: e.target.value === "true" });
        }}
      >
        <option value="">— kein Rücksprung —</option>
        <option value="true">Ja (true)</option>
        <option value="false">Nein (false)</option>
      </SelectInput>
    );
  }

  const options = sourceField?.valueOptions ?? [];
  if (options.length) {
    const normalized = !hasReset ? "" : String(value ?? "");
    const hasCurrent = options.some((opt) => opt.value === normalized);
    const dynamicOptions =
      hasCurrent || !normalized
        ? options
        : [{ value: normalized, label: `${normalized} (aktuell)` }, ...options];
    return (
      <SelectInput
        label={label}
        value={normalized}
        onChange={(e) => {
          if (e.target.value === "") onChange({ active: false });
          else onChange({ active: true, value: e.target.value });
        }}
      >
        <option value="">— kein Rücksprung —</option>
        {dynamicOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </SelectInput>
    );
  }

  const text = !hasReset ? "" : typeof value === "string" || typeof value === "number" ? String(value) : JSON.stringify(value ?? "");
  return (
    <TextInput
      label={`${label} (leer = kein Rücksprung)`}
      value={text}
      placeholder="Rücksprung-Wert"
      onChange={(e) => {
        const raw = e.target.value;
        if (!raw.trim()) onChange({ active: false });
        else onChange({ active: true, value: parseRuleValue(raw) });
      }}
    />
  );
}
